import React, { useRef, useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useStoreState } from '../store';
import { useWindowOrientation } from '../hooks/useWindowOrientation';
import { SubtitleTextColors, SubtitleTextWeights } from '../typings';
import { getOrientationSubtitleTextSize } from '../utils/subtitle-text-sizes';

export const SubtitleOverlay = () => {
  const subtitleOverlayRef = useRef<HTMLDivElement | null>(null);
  const videoElement = useStoreState(state => state.player.videoElement);
  const videoEngine = useStoreState(state => state.player.videoEngine);
  const controlsVisible = useStoreState(state => state.player.controlsVisible);
  const activeSubtitle = useStoreState(state => state.subtitles.activeSubtitle);
  const subtitleTextColor = useStoreState(state => state.subtitles.subtitleTextColor);
  const subtitleTextSize = useStoreState(state => state.subtitles.subtitleTextSize);
  const subtitleTextWeight = useStoreState(state => state.subtitles.subtitleTextWeight);
  const currentTime = useStoreState(state => state.player.playbackCurrentTime);
  const castSession = useStoreState(state => state.chromecast.castSession);
  const windowOrientation = useWindowOrientation();

  useEffect(
    function renderSubtitles() {
      if (!subtitleOverlayRef.current) return;

      subtitleOverlayRef.current.innerHTML = '';

      if (!activeSubtitle?.cues || !videoEngine) return;

      let activeCues = Array.from(activeSubtitle.cues)
        .filter(cue => currentTime >= cue.startTime && currentTime <= cue.endTime)
        .sort((cueA, cueB) => cueA.startTime - cueB.startTime);

      if (!activeCues) return;

      Array.from(activeCues).forEach(cue => {
        const div = document.createElement('div');

        // @ts-ignore
        div.appendChild(cue.getCueAsHTML());

        div.style.color = subtitleTextColor;
        div.style.fontSize = getOrientationSubtitleTextSize(subtitleTextSize);
        div.style.fontWeight = subtitleTextWeight;
        div.style.padding = '0 0.2em';
        div.style.backgroundColor = '#000000a6';
        div.style.textAlign = 'center';
        div.style.borderRadius = '0.2em';
        div.style.marginTop = '0.2em';

        subtitleOverlayRef.current!.appendChild(div);
      });
    },
    [activeSubtitle, currentTime, subtitleTextColor, subtitleTextSize, subtitleTextWeight, windowOrientation]
  );

  if (castSession || !videoElement) return null;

  /**
   * Calculate the position of the subtitles layer
   * based on the video resolution */
  function getPadding() {
    let padding = 0;

    if (videoElement) {
      const { clientHeight, clientWidth, videoHeight, videoWidth } = videoElement;
      const aspectRatio = videoHeight / videoWidth;
      const scaledHeight = clientWidth * aspectRatio;

      if (clientHeight > scaledHeight) {
        const deltaHeight = clientHeight - scaledHeight;
        padding = deltaHeight / 2;
      } else {
        padding = controlsVisible ? 130 : 40;
      }
    }

    return padding;
  }

  return (
    <Container className="subtitles-overlay" padding={getPadding()} ref={subtitleOverlayRef}>
      <TextCueStyles
        subtitleTextColor={subtitleTextColor}
        subtitleTextSize={getOrientationSubtitleTextSize(subtitleTextSize)}
        subtitleTextWeight={subtitleTextWeight}
        padding={getPadding()}
      />
    </Container>
  );
};

interface VideoElementCustomProps {
  subtitleTextColor: SubtitleTextColors;
  subtitleTextSize: string;
  subtitleTextWeight: SubtitleTextWeights;
  padding: number;
}

const TextCueStyles = createGlobalStyle<VideoElementCustomProps>`
  .vjs-text-track-display {
    display: none !important;  
  }
  
  video::-webkit-media-text-track-container {
    display: none !important;  
  }
`;

export const Container = styled.div<{ padding: number }>`
  position: absolute;
  padding-bottom: 10px;
  top: ${p => p.padding}px;
  bottom: ${p => p.padding}px;
  left: 10px;
  right: 10px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
`;
